import React, { useEffect, useRef } from "react";
import "./HeroLoader.css";

// Minimum time the loader stays on screen, so a fast load doesn't read as a
// flash.
export const MIN_LOADER_MS = 1000;

// Never hold the page longer than this waiting on images; whatever hasn't
// loaded by then keeps loading in place.
export const MAX_LOADER_MS = 5000;

// How quickly the bar's drawn width catches up with its target, and how long
// it rests at 100% before the page is revealed.
const SMOOTHING_MS = 180;
const FINISH_SMOOTHING_MS = 90;
const FULL_HOLD_MS = 150;

// Browsers stop animation frames for background tabs and covered windows;
// once loading is done, reveal the page after this long regardless.
const COMPLETE_FALLBACK_MS = 1200;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const minLoaderDelay = () => delay(MIN_LOADER_MS);
export const maxLoaderDelay = () => delay(MAX_LOADER_MS);

// Downloads the given image URLs into the browser cache so the <img> tags
// that later use the exact same URLs render instantly. Failed images count
// as done. Reports progress as a 0–1 fraction.
export const preloadImages = (urls, onProgress) => {
  const unique = [...new Set(urls.filter(Boolean))];
  if (unique.length === 0) {
    onProgress?.(1);
    return Promise.resolve();
  }

  let done = 0;
  return Promise.all(
    unique.map(
      (url) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = img.onerror = () => {
            done += 1;
            onProgress?.(done / unique.length);
            resolve();
          };
          img.src = url;
        })
    )
  );
};

// The bar always creeps forward on its own (easing toward 90%) so it never
// sits still, jumps ahead when real progress is further along, and once
// `done` is set it glides to 100% and only then calls onComplete.
export default function HeroLoader({ progress = 0, done = false, onComplete }) {
  const fillRef = useRef(null);
  const progressRef = useRef(progress);
  const doneRef = useRef(done);
  const onCompleteRef = useRef(onComplete);
  const completedRef = useRef(false);

  progressRef.current = progress;
  doneRef.current = done;
  onCompleteRef.current = onComplete;

  const complete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current?.();
  };

  useEffect(() => {
    const start = performance.now();
    let last = start;
    let shown = 0;
    let rafId;
    let holdId;

    const tick = (now) => {
      const crawl = 0.9 * (1 - Math.exp(-(now - start) / 1500));
      const target = doneRef.current
        ? 1
        : Math.min(0.9, Math.max(progressRef.current, crawl));

      const smoothing = doneRef.current ? FINISH_SMOOTHING_MS : SMOOTHING_MS;
      shown += (target - shown) * Math.min(1, (now - last) / smoothing);
      last = now;

      if (doneRef.current && shown > 0.98) {
        fillRef.current.style.transform = "scaleX(1)";
        holdId = setTimeout(complete, FULL_HOLD_MS);
        return;
      }

      fillRef.current.style.transform = `scaleX(${shown})`;
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(holdId);
    };
  }, []);

  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(complete, COMPLETE_FALLBACK_MS);
    return () => clearTimeout(id);
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="hero-skeleton">
      <div className="hero-skeleton__label">Loading...</div>
      <div className="hero-skeleton__bar">
        <div className="hero-skeleton__fill" ref={fillRef} />
      </div>
    </div>
  );
}
